import React, {
  ChangeEvent,
  DragEvent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import TextareaAtom from '../../atoms/Textarea/TextareaAtom';
import './UploadAttachmentMolecule.scss';

const DEFAULT_MAX_SIZE_BYTES = 200 * 1024 * 1024;
const SIZE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

function formatSize(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    SIZE_UNITS.length - 1,
  );
  const value = bytes / Math.pow(1024, exponent);
  const decimals = value >= 10 ? 0 : 1;
  const rounded = exponent === 0 ? value.toString() : value.toFixed(decimals);
  return `${rounded} ${SIZE_UNITS[exponent]}`;
}

export interface UploadAttachmentMoleculeProps {
  sphereId?: string;
  onUpload: (file: File, description?: string) => Promise<unknown>;
  maxSizeBytes?: number;
  accept?: string;
  disabled?: boolean;
  className?: string;
}

const UploadAttachmentMolecule: React.FC<UploadAttachmentMoleculeProps> = ({
  sphereId,
  onUpload,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  accept,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [description, setDescription] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const maxSizeLabel = useMemo(() => formatSize(maxSizeBytes), [maxSizeBytes]);
  const interactive = !disabled && !isUploading;

  const validateAndSetFile = useCallback(
    (candidate: File | null): void => {
      if (!candidate) {
        setFile(null);
        return;
      }
      if (candidate.size > maxSizeBytes) {
        setError(t('attachments.errors.tooLarge', { maxSize: maxSizeLabel }));
        setFile(null);
        return;
      }
      setError(null);
      setFile(candidate);
    },
    [maxSizeBytes, maxSizeLabel, t],
  );

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selected = event.target.files?.[0] ?? null;
      validateAndSetFile(selected);
      // Reset so selecting the same file again still triggers onChange.
      event.target.value = '';
    },
    [validateAndSetFile],
  );

  const openFilePicker = useCallback(() => {
    if (!interactive) return;
    inputRef.current?.click();
  }, [interactive]);

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLButtonElement>) => {
      if (!interactive) return;
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(true);
    },
    [interactive],
  );

  const handleDragLeave = useCallback((event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (event: DragEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragOver(false);
      if (!interactive) return;
      const dropped = event.dataTransfer?.files?.[0] ?? null;
      validateAndSetFile(dropped);
    },
    [interactive, validateAndSetFile],
  );

  const handleClear = useCallback(() => {
    if (!interactive) return;
    setFile(null);
    setError(null);
    setDescription('');
  }, [interactive]);

  const handleSubmit = useCallback(async () => {
    if (!file || !interactive) return;
    setIsUploading(true);
    setError(null);
    try {
      const trimmed = description.trim();
      await onUpload(file, trimmed.length > 0 ? trimmed : undefined);
      setFile(null);
      setDescription('');
    } catch (err: unknown) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : t('attachments.uploadFailed');
      setError(message);
    } finally {
      setIsUploading(false);
    }
  }, [description, file, interactive, onUpload, t]);

  const rootClasses = [
    'upload-attachment-molecule',
    isDragOver ? 'upload-attachment-molecule--drag-over' : '',
    disabled ? 'upload-attachment-molecule--disabled' : '',
    isUploading ? 'upload-attachment-molecule--uploading' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const descriptionId = `upload-attachment-description-${sphereId ?? 'noscope'}`;

  return (
    <div className={rootClasses} data-sphere-id={sphereId}>
      <button
        type="button"
        className="upload-attachment-molecule__dropzone"
        aria-disabled={!interactive}
        aria-label={t('attachments.uploadHint')}
        onClick={openFilePicker}
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <i className="pi pi-cloud-upload" aria-hidden="true" />
        <div className="upload-attachment-molecule__dropzone-text">
          {file ? (
            <>
              <span className="upload-attachment-molecule__file-name">
                {file.name}
              </span>
              <span className="upload-attachment-molecule__file-size">
                {formatSize(file.size)}
              </span>
            </>
          ) : (
            <>
              <span>{t('attachments.uploadHint')}</span>
              <span className="upload-attachment-molecule__hint-secondary">
                {t('attachments.maxSizeHint', { maxSize: maxSizeLabel })}
              </span>
            </>
          )}
        </div>
      </button>
      <input
        ref={inputRef}
        type="file"
        className="upload-attachment-molecule__file-input"
        accept={accept}
        onChange={handleFileInputChange}
        disabled={!interactive}
        data-testid="upload-attachment-file-input"
      />

      {file && (
        <div className="upload-attachment-molecule__description">
          <label
            htmlFor={descriptionId}
            className="upload-attachment-molecule__label"
          >
            {t('attachments.labels.description')}
          </label>
          <TextareaAtom
            id={descriptionId}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t('attachments.placeholders.description')}
            rows={2}
            disabled={!interactive}
          />
        </div>
      )}

      {error && (
        <div className="upload-attachment-molecule__error" role="alert">
          <i className="pi pi-exclamation-circle" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      <div className="upload-attachment-molecule__actions">
        {file && !isUploading && (
          <ButtonAtom
            variant="text"
            label={t('common.actions.cancel')}
            onClick={handleClear}
            disabled={!interactive}
          />
        )}
        <ButtonAtom
          variant="primary"
          icon={isUploading ? 'pi pi-spin pi-spinner' : 'pi pi-upload'}
          label={isUploading ? t('attachments.uploading') : t('attachments.upload')}
          onClick={handleSubmit}
          disabled={!file || !interactive}
        />
      </div>
    </div>
  );
};

export default UploadAttachmentMolecule;
